import React, { useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Container,
  Form,
  FloatingLabel,
  Button,
  Table,
  Modal,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { AshaBlockServices } from "../../services/AshaBlocks";
import { PHCServices } from "../../services/Phcs";
import { TalukServices } from "../../services/Taluk";
import { AshaServices } from "../../services/AshaService";
import { LoadingModal } from "../components/Index.js";
import { Capitalize } from "../components/utils";

function AddAsha({ onHide, onClose, ...props }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(true);
  const [selectedOption, setSelectedOption] = useState("individual");
  const [taluks, setTaluks] = useState([]);
  const [phcs, setPHCs] = useState([]);
  const [ablocks, setAblocks] = useState([]);
  const [jsonData, setJsonData] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    preDefinedAshaId: "",
    populationAlloted: "",
    noOfHousesAlloted: "",
    mobileNo: "",
    village: "",
    phco: "",
    hio: "",
    cho: "",
    ashaBlockId: undefined,
  });
  const [error, setError] = useState({});
  const [selectedTalukId, setSelectedTalukId] = useState("");
  const [selectedPhcId, setSelectedPhcId] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  const { isSuperAdmin, isTaluk, isPhc, isAshaBlock } = useMemo(
    () => ({
      isSuperAdmin: user?.roleDetails?.role === "Super Admins",
      isTaluk: user?.roleDetails?.role === "Taluk",
      isPhc: user?.roleDetails?.role === "PHC",
      isAshaBlock: user?.roleDetails?.role === "Asha Block",
    }),
    [user]
  );

  useEffect(() => {
    if (isSuperAdmin) {
      getAsha();
      getTaluks();
      getPhcs();
      getAshaBlocks(); // all asha blocks
    } else if (isTaluk) {
      getAsha();
      getPhcs(user?.talukDetails?.id);
      getAshaBlocks(user?.talukDetails?.id); // filtered by Taluk
    } else if (isPhc) {
      getAsha();
      getAshaBlocks(null, user?.phcDetails?.id); // filtered by PHC
    } else if (isAshaBlock) {
      getAsha(); // no dropdowns needed
    }
    
  }, []);

  useEffect(() => {
    if (selectedTalukId) {
      getPhcs(selectedTalukId);
    } else {
      setPHCs([]);
    }
    setAblocks([]); // Reset AshaBlocks when Taluk changes
    setSelectedPhcId("");
    setFormData((prev) => ({ ...prev, ashaBlockId: undefined }));
  }, [selectedTalukId]);
  
  useEffect(() => {
    if (selectedPhcId) {
      getAshaBlocks(null, selectedPhcId);
    } else {
      setAblocks([]);
    }
    setFormData((prev) => ({ ...prev, ashaBlockId: undefined }));
  }, [selectedPhcId]);
  const getAsha = async () => {
    try {
      await AshaServices.getAllAshas(); // Load Asha list if needed
    } catch (error) {
      console.error("Failed to load Ashas", error);
    }
  };

  const getTaluks = async () => {
    try {
      const res = await TalukServices.getAllTaluks();
      if (Array.isArray(res)) {
        setTaluks(res);
      } else if (res && typeof res === "object") {
        const values = Object.values(res);
        if (values.length > 0 && values[0].id) {
          setTaluks(values);
        } else {
          setTaluks([]);
        }
      } else {
        setTaluks([]);
      }
    } catch (error) {
      console.error("Failed to load Taluks", error);
      setTaluks([]);
    }
  };

  const getAshaBlocks = async (talukId = null, phcId = null) => {
    try {
      let res;
      if (phcId) {
        res = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      } else if (talukId) {
        res = await AshaBlockServices.getAshaBlockByTalukId(talukId); // Ensure this method exists
      } else {
        res = await AshaBlockServices.getAllAshaBlocks();
      }
      if (Array.isArray(res)) {
        setAblocks(res);
      } else if (res && typeof res === "object") {
        const values = Object.values(res);
        if (values.length > 0 && values[0].id) {
          setAblocks(values);
        } else {
          setAblocks([]);
        }
      } else {
        setAblocks([]);
      }
    } catch (error) {
      console.error("Failed to load AshaBlocks", error);
      setAblocks([]);
    }
  };

  const getPhcs = async (talukId = null) => {
    try {
      const res = talukId
        ? await PHCServices.gePhcsByTalukId(talukId)
        : await PHCServices.getAllPhcs();
        if (Array.isArray(res)) {
          setPHCs(res);
        } else if (res && typeof res === "object") {
          const values = Object.values(res);
          if (values.length > 0 && values[0].id) {
            setPHCs(values);
          } else {
            setPHCs([]);
          }
        } else {
          setPHCs([]);
        }
      } catch (error) {
        console.error("Failed to load PHCs", error);
        setPHCs([]);
      }
  };

  const getAshaBlocksByPhcId = async (phcId) => {
    try {
      const res = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      setAblocks(res.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: Capitalize(value) });
  };

  const validateForm = () => {
    let tempError = {};
    let valid = true;

    if (!formData.name) {
      tempError.name = "Name is required";
      valid = false;
    }
    if (!formData.preDefinedAshaId) {
      tempError.preDefinedAshaId = "Asha ID is required";
      valid = false;
    }
    if (!formData.populationAlloted) {
      tempError.populationAlloted = "Required Field";
      valid = false;
    }
    if (!formData.mobileNo || !/^[0-9]{10}$/.test(formData.mobileNo)) {
      tempError.mobileNo = "Mobile number is required and must be 10 digits";
      valid = false;
    }
    if (!formData.village) {
      tempError.village = "Village is required";
      valid = false;
    }
    if (!formData.ashaBlockId) {
      tempError.ashaBlockId = "Asha Block is required";
      valid = false;
    }

    setError(tempError);
    return valid;
  };

  const submitAsha = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await AshaServices.addAsha(formData);
      if (res.status === 200) {
        alert(res.message);
        setFormData({
          name: "",
          preDefinedAshaId: "",
          populationAlloted: "",
          noOfHousesAlloted: "",
          mobileNo: "",
          village: "",
          phco: "",
          hio: "",
          cho: "",
          ashaBlockId: undefined,
        });
        onHide();
        onClose();
        navigate("/asha");
      } else {
        alert("Error while adding");
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      if (parsedData.length > 0 && Object.keys(parsedData[0]).length === 10) {
        const ashaArray = parsedData.map((entry) => ({
          name: entry["Name"] || null,
          preDefinedAshaId: Number(entry["Asha ID"]) || 0,
          mobileNo: Number(entry["Mobile no"]) || 0,
          village: entry["Village"] || null,
          phco: entry["PHCO"] || null,
          hio: entry["HIO"] || null,
          cho: entry["CHO"] || null,
          ashaBlockName: entry["Asha Block"] || null,
          populationAlloted: Number(entry["Population Allotted"]) || null,
        }));

        const valid = ashaArray.every(
          ({
            name,
            preDefinedAshaId,
            mobileNo,
            village,
            populationAlloted,
            ashaBlockName,
          }) =>
            name &&
            preDefinedAshaId &&
            mobileNo &&
            village &&
            populationAlloted &&
            ashaBlockName
        );

        if (valid) {
          setJsonData(ashaArray);
        } else {
          alert("Enter mandatory field");
        }
      } else {
        alert("Error: The uploaded Excel file must contain exactly 10 columns.");
      }
    };
  };

  const onBulkAdd = async () => {
    setLoading(true);
    try {
      const res = await AshaServices.bulkAddAsha(jsonData);
      if (res.status === 200) {
        alert(res.message);
        setJsonData([]);
        onHide();
        onClose();
        navigate("/asha");
      } else {
        alert("Error during bulk add");
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal {...props} size="lg" centered fullscreen={fullscreen} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Add Asha</Modal.Title>
      </Modal.Header>
      <Container className="mt-4">
        {/* Removed radio button selection step - directly show individual form */}

        {/* {selectedOption === "excel" && (
          <>
            <Form.Group controlId="formFile" className="mt-3">
              <Form.Label>Upload Excel File</Form.Label>
              <Form.Control
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileUpload}
              />
            </Form.Group>

            {jsonData.length > 0 && (
              <Table striped bordered hover className="mt-3">
                <thead>
                  <tr>
                    {Object.keys(jsonData[0]).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jsonData.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).map((value, i) => (
                        <td key={i}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Row className="mt-3">
              <Col>
                <Button onClick={onBulkAdd}>Add</Button>
              </Col>
              <Col className="text-end">
                <Button onClick={onHide}>Cancel</Button>
              </Col>
            </Row>
          </>
        )} */}

        <Form className="mt-3">
            <Row>
              <Col>
                <FloatingLabel controlId="ashaId" label="Asha ID*">
                  <Form.Control
                    type="text"
                    name="preDefinedAshaId"
                    value={formData.preDefinedAshaId}
                    onChange={handleInputChange}
                    isInvalid={!!error.preDefinedAshaId}
                  />
                </FloatingLabel>
              </Col>
              <Col>
                <FloatingLabel controlId="name" label="Name*">
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    isInvalid={!!error.name}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <FloatingLabel
                  controlId="populationAlloted"
                  label="Population Allotted*"
                >
                  <Form.Control
                    type="text"
                    name="populationAlloted"
                    value={formData.populationAlloted}
                    onChange={handleInputChange}
                    isInvalid={!!error.populationAlloted}
                  />
                </FloatingLabel>
              </Col>
              <Col>
                <FloatingLabel controlId="noOfHousesAlloted" label="No.of houses alloted">
                  <Form.Control
                    type="text"
                    name="noOfHousesAlloted"
                    value={formData.noOfHousesAlloted}
                    onChange={handleInputChange}
                    isInvalid={!!error.noOfHousesAlloted}
                  />
                </FloatingLabel>
              </Col>
              <Col>
                <FloatingLabel controlId="village" label="Village*">
                  <Form.Control
                    type="text"
                    name="village"
                    value={formData.village}
                    onChange={handleInputChange}
                    isInvalid={!!error.village}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <FloatingLabel controlId="mobileNo" label="Mobile*">
                  <Form.Control
                    type="text"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={handleInputChange}
                    isInvalid={!!error.mobileNo}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <FloatingLabel controlId="phco" label="PHCO">
                  <Form.Control
                    name="phco"
                    value={formData.phco}
                    onChange={handleInputChange}
                  />
                </FloatingLabel>
              </Col>
              <Col>
                <FloatingLabel controlId="hio" label="HIO">
                  <Form.Control
                    name="hio"
                    value={formData.hio}
                    onChange={handleInputChange}
                  />
                </FloatingLabel>
              </Col>
              <Col>
                <FloatingLabel controlId="cho" label="CHO">
                  <Form.Control
                    name="cho"
                    value={formData.cho}
                    onChange={handleInputChange}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mt-3">
  {isSuperAdmin && (
    <Col>
      <FloatingLabel controlId="taluk" label="Taluk*">
        <Form.Select
          value={selectedTalukId}
          onChange={(e) => setSelectedTalukId(e.target.value)}
        >
          <option value="">Select</option>
          {taluks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.taluk}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>
    </Col>
  )}

  {(isSuperAdmin || isTaluk) && (
    <Col>
      <FloatingLabel controlId="phc" label="PHC*">
        <Form.Select
          value={selectedPhcId}
          onChange={(e) => setSelectedPhcId(e.target.value)}
        >
          <option value="">Select</option>
          {phcs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.phc}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>
    </Col>
  )}

  {(isSuperAdmin || isTaluk || isPhc) && (
    <Col>
      <FloatingLabel controlId="ashaBlockId" label="Asha Block*">
        <Form.Select
          name="ashaBlockId"
          value={formData.ashaBlockId || ""}
          onChange={(e) =>
            setFormData({ ...formData, ashaBlockId: e.target.value })
          }
          isInvalid={!!error.ashaBlockId}
        >
          <option value="">Select</option>
          {ablocks.map((a) => (
            <option key={a.id} value={a.id}>
              {a.ashaBlock}
            </option>
          ))}
        </Form.Select>
      </FloatingLabel>
    </Col>
  )}
</Row>
            <Row className="mt-4">
              <Col>
              <div className="d-flex justify-content-end gap-2">
      <Button onClick={submitAsha} variant="success">Add</Button>
      <Button onClick={onHide} variant="danger">Cancel</Button>
    </div>
              </Col>
            </Row>
          </Form>
      </Container>
      {loading && <LoadingModal />}
    </Modal>
  );
}

export default AddAsha;
